import {
    DnsWriteError,
    DnsWriteErrorKind,
} from '../../zones/types';
import {classifyWriteError} from '../../zones/services/writeErrors';

// FNP-1193 — CLASSIFYING A REFUSED CERTIFICATE REQUEST.
//
// ⚠️ WHY THE SHARED CLASSIFIER IS NOT ENOUGH ON THIS PATH, AND THIS FILE EXISTS.
//
// `classifyWriteError` recovers a kind from the `KIND: ` message prefix the DNS
// contract's own base constructor builds, falling back to the class name in
// `errorType`. Both work because the DNS side throws branded errors that
// survive the trip.
//
// The certificate Lambda does not. Verified in flexi-domains-api on 2026-08-29:
//
//   - `ConnectionNotUsableError` and `CertificateRequestError`
//     (src/certificate/src/request.ts:44-45) are BARE `extends Error` — no
//     kind property, no code, no `KIND: ` message prefix;
//   - the resolver calls `util.appendError(error.message, error.type, result)`,
//     and for a plain Error thrown inside a Lambda `error.type` is AppSync's
//     generic `Lambda:Unhandled`, not a domain string;
//   - the only hand-set errorTypes on this field are `Unauthorized` and
//     `NotFound`, neither of which is a permission refusal.
//
// So the single most likely refusal — the tenant's role lacks
// `acm:RequestCertificate` — reaches the browser as `Lambda:Unhandled` with a
// prose message, and `classifyWriteError` correctly answers `UNKNOWN`. Showing
// "no recognisable reason came back" for the one failure with a precise,
// actionable fix would be the worst outcome this module could produce.
//
// ⚠️ MATCHING PROSE IS A KNOWN-FRAGILE SEAM, AND IT IS DELIBERATELY NARROW.
//
// This matches ONE stable literal that the backend constructs itself, at the
// start of its message:
//
//     'This connection cannot request certificates. The tenant grants
//      acm:RequestCertificate in the role this connection assumes; a role
//      created from the DNS-only instructions does not have it.'
//
// That sentence is written by Flexi's own code (request.ts:165-169), not by
// ACM, so it changes only when someone edits this platform — unlike the ACM
// AccessDenied text behind the same fix, which is AWS's to reword and is NOT
// matched here.
//
// The proper fix is a domain errorType on the certificate Lambda's refusals,
// which is a backend change. Until then this is the seam, it is confined to
// this file, and it fails SAFE: an unmatched message falls through to the
// shared classifier and its honest UNKNOWN.

// The sentence the backend refuses with when the connection is not declared
// writable. Anchored at the START — a bare `includes` would also match a
// message that merely quotes it.
const NOT_DECLARED_WRITABLE_PREFIX = 'This connection cannot request certificates.';

// The adapter's own equivalent, for a caller that reached it directly. Same
// cause, same fix, different wording.
const NOT_DECLARED_WRITABLE_ADAPTER_PREFIX =
    'This connection is not declared able to request certificates.';

const messagesOf = (e: unknown): string[] => {

    const messages: string[] = [];

    if (e && typeof e === 'object' && 'errors' in e) {
        const {errors} = e as { errors?: unknown };
        if (Array.isArray(errors)) {
            errors.forEach(entry => {
                const message = (entry as { message?: unknown })?.message;
                if (typeof message === 'string') {
                    messages.push(message);
                }
            });
        }
    }

    if (e instanceof Error && typeof e.message === 'string') {
        messages.push(e.message);
    }

    return messages;
};

/**
 * Classify a rejected `requestCertificate`.
 *
 * Returns the shared `DnsWriteError` shape so every consumer — the copy table,
 * the inline alert, the notification — stays on one taxonomy rather than
 * growing a second one for certificates.
 *
 * ⚠️ THE ONE THING THIS ADDS over the shared classifier is recognising the
 * backend's own "not declared writable" refusal as PERMISSION_DENIED. Everything
 * else is delegated unchanged.
 */
export const classifyRequestError = (e: unknown): DnsWriteError => {

    const messages = messagesOf(e);

    const notDeclaredWritable = messages.find(message =>
        message.startsWith(NOT_DECLARED_WRITABLE_PREFIX)
        || message.startsWith(NOT_DECLARED_WRITABLE_ADAPTER_PREFIX));

    if (notDeclaredWritable) {
        return {
            kind: 'PERMISSION_DENIED' satisfies DnsWriteErrorKind,
            // The backend's own sentence, VERBATIM. It already names the exact
            // IAM action and where the grant belongs, which is more specific
            // than any copy this module could write for it.
            providerMessage: notDeclaredWritable,
        };
    }

    // ⚠️ EVERYTHING ELSE GOES TO THE SHARED CLASSIFIER UNCHANGED, including an
    // ACM AccessDenied that arrives with a `PERMISSION_DENIED: ` prefix from a
    // branded provider error. Only the unbranded Flexi-side refusal above needs
    // special handling; duplicating the rest here is how the two copies diverge.
    return classifyWriteError(e);
};
